import type { ComponentProps, Ref, RefCallback, RefObject } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
// eslint-disable-next-line no-restricted-imports
import type { Link as RRDLink } from 'react-router-dom';
import { UNSAFE_DataRouterContext, matchRoutes } from 'react-router-dom';

function mergeRefs<T = any>(
  ...refs: Array<RefObject<T> | Ref<T> | undefined>
): RefCallback<T> {
  return value => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref != null) {
        (ref as RefObject<T | null>).current = value;
      }
    });
  };
}

type PreloadRoute =
  | {
      routeToFile: Record<string, string>;
      fileToImports: Record<string, string[]>;
    }
  | undefined;

function unique<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}

function getFilesFromMatch(
  matches: NonNullable<ReturnType<typeof matchRoutes>>
) {
  return unique(
    matches.flatMap((match, i) =>
      [
        i === matches.length - 1 ? match.route.handle?.page : null,
        match.route.handle?.layout,
      ].filter(Boolean)
    )
  );
}

type Prefetch =
  // Explicitly opt out of prefetch
  | null
  // Prefetch on link render
  | 'render'
  // Prefetch on hover
  | 'intent';

export const usePrefetch = <T extends HTMLElement>(
  ref?: RefObject<T> | Ref<T>,
  prefetch: Prefetch = 'intent'
) => {
  const innerRef = useRef<T>(null);
  const mergedRef = mergeRefs(ref, innerRef);
  const [shouldPrefetch, setShouldPrefetch] = useState(false);

  useEffect(() => {
    const node = innerRef.current;
    if (node && prefetch === 'intent') {
      const cb = () => {
        setShouldPrefetch(true);
      };
      node.addEventListener('pointerenter', cb);
      return () => node.removeEventListener('pointerenter', cb);
    }
    return () => {};
  }, [prefetch]);

  return [mergedRef, prefetch === 'render' ? true : shouldPrefetch] as const;
};

export const PrefetchLink = ({
  to,
}: Pick<ComponentProps<typeof RRDLink>, 'to'>) => {
  const { router } = useContext(UNSAFE_DataRouterContext) ?? {};
  // @ts-expect-error preloadRoute
  const { preloadRoutes }: { preloadRoutes: PreloadRoute } = window;
  if (preloadRoutes && router) {
    const match = matchRoutes(router.routes, to);
    if (match) {
      const files = getFilesFromMatch(match);
      const outputFiles = files.map(file => preloadRoutes.routeToFile[file]);
      const imports = new Set(outputFiles);
      for (const f of outputFiles) {
        for (const i of preloadRoutes.fileToImports[f]) {
          imports.add(i);
        }
      }
      return (
        <Helmet>
          {[...imports].map(e => (
            <link key={e} href={`/${e}`} rel="modulepreload" />
          ))}
        </Helmet>
      );
    }
  }
  return null;
};
